import { createContext, useContext, type ReactNode } from 'react';
import { useCopyFeedback, type WriteClipboard } from '../shared/use_copy_feedback.js';
import type { PhotosFor } from '../shared/use_traveler_photos.js';
import type { VoucherCopy } from './voucher_copy.js';

/** What every voucher document receives from its host, besides the view: the chrome copy and
 *  the clipboard. */
export interface VoucherHostProps {
  copy: VoucherCopy;
  /** Copies a code or an address; resolves `true` when it copied. */
  writeClipboard: WriteClipboard;
  /** The copy failure the screen shows — the host's telemetry. */
  onCopyFailed?: () => void;
  /** The travelers' photos by profile id; absent, the avatars keep the initials. */
  photosFor?: PhotosFor;
}

/** How the document sits in its container. `card`: loose on a page, with its own frame. `panel`:
 *  inside a side panel that is already the surface (background, free side, shadow, the device
 *  cut-out inset) — the document only takes the height and drops its card frame. */
export type VoucherFit = 'card' | 'panel';

/** The panel fit: the overrides `cn` merges over the document's own frame. */
const PANEL_FIT = 'min-h-0 flex-1 max-h-none max-w-none rounded-none border-0 shadow-none';

export const fitClass = (fit: VoucherFit | undefined): string | undefined =>
  fit === 'panel' ? PANEL_FIT : undefined;

const VoucherEnvContext = createContext<VoucherHostProps | null>(null);

/** Hands the host props to the parts of one document, so the copy and the clipboard do not
 *  travel through every field. */
export function VoucherEnvProvider({
  env,
  children,
}: Readonly<{ env: VoucherHostProps; children: ReactNode }>) {
  return <VoucherEnvContext.Provider value={env}>{children}</VoucherEnvContext.Provider>;
}

/** The host props of the document in scene. Outside a document it is a mounting error, not a
 *  state: every part here only exists inside one. */
export function useVoucherEnv(): VoucherHostProps {
  const env = useContext(VoucherEnvContext);
  if (!env) throw new Error('voucher: part mounted outside a voucher document');
  return env;
}

export const useVoucherCopy = (): VoucherCopy => useVoucherEnv().copy;

/** `useCopyFeedback` on the host's clipboard. */
export function useVoucherCopyFeedback(): ReturnType<typeof useCopyFeedback> {
  const { writeClipboard, onCopyFailed } = useVoucherEnv();
  return useCopyFeedback(writeClipboard, onCopyFailed);
}
